import type { ReactNode } from 'react';
import type { FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { SectionCard } from '@/components/common/SectionCard';
import { Badge } from '@/components/ui/Badge';
import { FieldLabel } from '@/components/forms/FieldLabel';
import { TextField } from '@/components/forms/TextField';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { FIELD_HELPERS } from '@/features/startups/constants';
import { STARTUP_PROFILE_STRINGS } from './startup-profile.strings';
import { FileUploadField } from '@/components/common/FileUploadField';
import type { StartupProfileData } from '../../api/startup-profile.api';
import type { StartupProfileFormValues } from './schema';

const S = UI_STRINGS.STARTUP_PROFILE;

interface SectionProps {
  /** View mode only: extra header content, e.g. the create wizard review's Edit link. */
  headerRight?: ReactNode;
  mode: 'view' | 'edit';
  data: StartupProfileData;
  register?: UseFormRegister<StartupProfileFormValues>;
  watch?: UseFormWatch<StartupProfileFormValues>;
  setValue?: UseFormSetValue<StartupProfileFormValues>;
  errors?: FieldErrors<StartupProfileFormValues>;
  onUploadingChange?: (isUploading: boolean) => void;
}

/** Pitch Materials card: pitch deck (view: link; edit: upload) and pitch video URL. */
export function PitchMaterialsSection({
  mode,
  data,
  register,
  watch,
  setValue,
  errors,
  onUploadingChange,
  headerRight,
}: SectionProps) {
  if (mode === 'view' || !register || !watch || !setValue) {
    return (
      <SectionCard title={S.PITCH_MATERIALS} headerRight={headerRight}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <dt>
              <FieldLabel label={S.PITCH_DECK} variant="caps" />
            </dt>
            {/* `pitchDeckUrl` holds the stored S3 KEY, so it answers "is there
                a deck?" but is NOT a link target. The href must be the
                presigned `pitchDeckDownloadUrl`, which is null when signing
                failed - hence the separate link/badge conditions (MYN-101). */}
            {data.pitchDeckUrl ? (
              <>
                {data.pitchDeckDownloadUrl ? (
                  <a
                    href={data.pitchDeckDownloadUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-dkgreen hover:text-green text-p6 font-semibold"
                  >
                    {S.VIEW_PITCH_DECK}
                  </a>
                ) : (
                  <p className="text-slate text-p6">{STARTUP_PROFILE_STRINGS.LINK_UNAVAILABLE}</p>
                )}
                <div className="mt-2">
                  <Badge variant="success">{S.VIEWABLE_BY_RECIPIENTS}</Badge>
                </div>
              </>
            ) : (
              <p className="text-slate text-p6">{S.NOT_ADDED}</p>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <dt>
              <FieldLabel label={S.PITCH_VIDEO_URL} variant="caps" />
            </dt>
            {/* An external link the founder supplied (A-58), not an upload -
                so it goes straight into the href with no presigning. */}
            {data.pitchVideoUrl ? (
              <a
                href={data.pitchVideoUrl}
                target="_blank"
                rel="noreferrer"
                className="text-dkgreen hover:text-green text-p6 font-semibold"
              >
                {S.VIEW_PITCH_VIDEO}
              </a>
            ) : (
              <p className="text-slate text-p6">{S.NOT_ADDED}</p>
            )}
          </div>
        </div>
      </SectionCard>
    );
  }

  const hasPitchDeck = Boolean(watch('pitchDeckFileKey'));

  return (
    <SectionCard title={S.PITCH_MATERIALS}>
      <div className="flex flex-col gap-5">
        {/* Deck and its badge are one group with a tighter gap, so the badge
            reads as belonging to the control above it rather than as the next
            field down. */}
        <div className="flex flex-col gap-2">
          <FileUploadField
            label={S.PITCH_DECK}
            hint={FIELD_HELPERS.pitchDeck}
            kind="pitchDeck"
            accept="application/pdf"
            uploaded={hasPitchDeck}
            error={errors?.pitchDeckFileKey?.message}
            onUploaded={(result) => {
              setValue('pitchDeckFileKey', result.fileKey, { shouldDirty: true });
              setValue('pitchDeckFileSizeBytes', result.sizeBytes, { shouldDirty: true });
            }}
            onClear={() => {
              setValue('pitchDeckFileKey', '', { shouldDirty: true });
              setValue('pitchDeckFileSizeBytes', undefined, { shouldDirty: true });
            }}
            onUploadingChange={onUploadingChange}
          />
          {/* FR-20 requires this badge in BOTH view and edit mode - a founder
              deciding what to attach needs to know the deck is shared, at the
              moment they attach it, not only afterwards. */}
          {hasPitchDeck && (
            <div>
              <Badge variant="success">{S.VIEWABLE_BY_RECIPIENTS}</Badge>
            </div>
          )}
        </div>
        {/* A link, not an upload (A-58): the client sheet types this field
            "Pitch Video Link | Optional | URL", and Section 6 keeps direct
            video upload out of scope. Mirrors BasicDetails.tsx's URL fields. */}
        <TextField
          {...register('pitchVideoUrl')}
          label={S.PITCH_VIDEO_URL}
          type="url"
          placeholder={STARTUP_PROFILE_STRINGS.PITCH_VIDEO_URL_PLACEHOLDER}
          error={errors?.pitchVideoUrl?.message}
        />
      </div>
    </SectionCard>
  );
}
